import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Spinner,
  Row,
  Col,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useGetPrimaryCompanyQuery,
  useGetPrimaryCompanyByIdQuery,
  useUpdatePrimaryCompanyMutation,
  useCreatePrimaryCompanyMutation,
} from "slices/pageAccess/PageAccessSlice";
import { UncontrolledTooltip } from "reactstrap";

const validationSchema = Yup.object({
  title: Yup.string().required("Company name is required"),
  company_name: Yup.string().required("Display Name is required"),
  company_code: Yup.string().required("Company Code is required"),
  address1: Yup.string().required("Address 1 is required"),
  contact_number: Yup.string().required("Contact Number is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

const PrimaryCompanyTab: React.FC = () => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [logoLoading, setLogoLoading] = useState<boolean>(false);
  const [logominiLoading, setLogominiLoading] = useState<boolean>(false);
  const [headerPdf, setHeaderPdf] = useState<boolean>(false);
  const [footerPdf, setFooterPdf] = useState<boolean>(false);

  const {
    data: primaryCompanyData,
    refetch,
    isLoading,
  } = useGetPrimaryCompanyQuery(undefined);
  const { data: companyDetails } = useGetPrimaryCompanyByIdQuery(
    selectedCompanyId,
    { skip: !selectedCompanyId },
  );
  const [updatePrimaryCompany, { isLoading: companyLoading }] =
    useUpdatePrimaryCompanyMutation();
  const [createPrimaryCompany, { isLoading: postCompanyLoading }] =
    useCreatePrimaryCompanyMutation();
  const [logoError, setLogoError] = useState<string | null>(null); // State for logo error
  const [miniLogoError, setMiniLogoError] = useState<string | null>(null); // State for mini logo error
  const [pdfLogoError, setPdfLogoError] = useState<string | null>(null); // State for pdf Header logo
  const [pdfFooterLogoError, setPdfFooterLogoError] = useState<string | null>(
    null,
  ); // State for pdf Footer logo

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    console.log("mini-logo function");
    setLogoLoading(true);

    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 304 || img.height !== 86) {
          setLogoError("Image dimensions must be 304x86 pixels.");
          return;
        }

        try {
          const formData = new FormData();
          formData.append("logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setLogoError("");
          refetch(); // Refresh the data to show the new logo
          setLogoLoading(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setLogoLoading(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // company_logo_mini

  const handleminilogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    console.log("mini-logo function");
    setLogominiLoading(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 458 || img.height !== 461) {
          setMiniLogoError("Image dimensions must be 458x461 pixels.");
          return;
        }

        try {
          const formData = new FormData();
          formData.append("company_logo_mini", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setMiniLogoError("");
          refetch(); // Refresh the data to show the new logo
          setLogominiLoading(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setLogominiLoading(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // pdf_report_logo

  const handlepdflogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setHeaderPdf(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 1088 || img.height !== 179) {
          setPdfLogoError("Image dimensions must be 1088x179 pixels.");
          return;
        }

        try {
          const formData = new FormData();
          formData.append("pdf_report_logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setPdfLogoError("");
          refetch(); // Refresh the data to show the new logo
          setHeaderPdf(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setHeaderPdf(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  //

  const handlefooterlogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setFooterPdf(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 1025 || img.height !== 110) {
          setPdfFooterLogoError("Image dimensions must be 1025x110 pixels.");
          return;
        }

        try {
          const formData = new FormData();
          formData.append("pdf_footer_logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setPdfFooterLogoError("");
          refetch(); // Refresh the data to show the new logo
          setFooterPdf(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setFooterPdf(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // Add these delete functions near your other file upload handlers
  const handleDeleteLogo = async (companyId: number) => {
    try {
      setLogoLoading(true);
      const formData = new FormData();
      formData.append("clear_logo", "true"); // Send 'null' as string to clear the logo
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setLogoError(null);
      refetch();
      setLogoLoading(false);
    } catch (error) {
      console.error("Error deleting logo:", error);
      setLogoLoading(false);
    }
  };

  const handleDeleteMiniLogo = async (companyId: number) => {
    try {
      setLogominiLoading(true);
      const formData = new FormData();
      formData.append("clear_company_logo_mini", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setMiniLogoError(null);
      refetch();
      setLogominiLoading(false);
    } catch (error) {
      console.error("Error deleting mini logo:", error);
      setLogominiLoading(false);
    }
  };

  const handleDeletePdfLogo = async (companyId: number) => {
    try {
      setHeaderPdf(true);
      const formData = new FormData();
      formData.append("clear_pdf_report_logo", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setPdfLogoError(null);
      refetch();
      setHeaderPdf(false);
    } catch (error) {
      console.error("Error deleting PDF logo:", error);
      setHeaderPdf(false);
    }
  };

  const handleDeletePdfFooterLogo = async (companyId: number) => {
    try {
      setFooterPdf(true);
      const formData = new FormData();
      formData.append("clear_pdf_footer_logo", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setPdfFooterLogoError(null);
      refetch();
      setFooterPdf(false);
    } catch (error) {
      console.error("Error deleting PDF footer logo:", error);
      setFooterPdf(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      company_name: "",
      company_code: "",
      address1: "",
      address2: "",
      country: "",
      zip_code: "",
      contact_number: "",
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (selectedCompanyId) {
        await updatePrimaryCompany({
          id: selectedCompanyId,
          updateData: values,
        }).unwrap();
        refetch();
        toggleModal();
      } else {
        await createPrimaryCompany({
          newPrimaryCompany: values,
        }).unwrap();
        refetch();
        toggleModal();
      }
    },
  });

  // Effect to set form values when companyDetails changes
  useEffect(() => {
    if (companyDetails) {
      formik.setValues({
        title: companyDetails.title || "",
        company_name: companyDetails.company_name || "",
        company_code: companyDetails.company_code || "",
        address1: companyDetails.address1 || "",
        address2: companyDetails.address2 || "",
        country: companyDetails.country || "",
        zip_code: companyDetails.zip_code || "",
        contact_number: companyDetails.contact_number || "",
        email: companyDetails.email || "",
      });
    }
  }, [companyDetails]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleEditCompany = (companyId: number) => {
    setSelectedCompanyId(companyId);
    toggleModal();
  };

  const handleAddCompany = () => {
    setSelectedCompanyId(null);
    toggleModal();
  };

  return (
    <div className="container mt-4">
      <h5 className="mb-4">Company Profile</h5>
      {isLoading ? (
        <Spinner />
      ) : (
        <Row>
          {primaryCompanyData?.length === 0 && (
            <Button
              onClick={() => handleAddCompany()}
              className="btn btn-soft-info btn-md shadow-none"
            >
              Add Company Information
            </Button>
          )}

          {primaryCompanyData?.length > 0 ? (
            primaryCompanyData.map((c: any) => (
              <Col md={10} sm={8} key={c.id} className="mb-4">
                <Card>
                  <CardHeader className="d-flex align-items-center">
                    <i className="ri-building-fill fs-4"></i>
                    <h4 className="card-title flex-grow-1 mb-0">
                      {" "}
                      Primary Company
                    </h4>
                    <div className="flex-shrink-0">
                      <Button
                        onClick={() => handleEditCompany(c.id!)}
                        className="btn btn-soft-info btn-md shadow-none"
                      >
                        Edit
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <table className="table mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-medium">Company Title</td>
                          <td>{c.title}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Display Name</td>
                          <td>{c.company_name}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Company Code</td>
                          <td>{c.company_code}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Address 1</td>
                          <td>{c.address1}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Address 2</td>
                          <td>{c.address2}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Country</td>
                          <td>{c.country}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Zip Code</td>
                          <td>{c.zip_code}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Contact Number</td>
                          <td>{c.contact_number}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Email</td>
                          <td>{c.email}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardBody>

                  <hr></hr>
                  <h5 className="mt-3">Company Logo</h5>
                  <span className="text-danger">{logoError}</span>
                  <div className="border-top p-3 bg-light">
                    <div className="text-center">
                      <label
                        htmlFor={`logo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && logoLoading ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.logo_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.logo_url}
                              alt="Company Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            {/* <small className="d-block mt-2">Click to change logo</small> */}
                            <span className="d-block btn btn-danger">
                              Click to change logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeleteLogo(c.id)}
                          >
                            Delete Company Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`logo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileUpload(e, c.id)}
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Mini-Logo</h5>
                  <span className="text-danger">{miniLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`minilogo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && logominiLoading ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.company_logo_mini_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.company_logo_mini_url}
                              alt="Company Mini Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to mini logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.company_logo_mini_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeleteMiniLogo(c.id)}
                          >
                            Delete Mini Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`minilogo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleminilogoFileUpload(e, c.id)
                        } /* Updated function name */
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Pdf Header Logo</h5>
                  <span className="text-danger">{pdfLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`pdflogo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && headerPdf ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.pdf_report_logo_url ? (
                          <>
                            <img
                              src={c.pdf_report_logo_url}
                              alt="Company Mini Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to Pdf Header logo
                            </span>
                          </>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.pdf_report_logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeletePdfLogo(c.id)}
                          >
                            Delete PDF Header Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`pdflogo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handlepdflogoFileUpload(e, c.id)
                        } /* Updated function name */
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Pdf Footer Logo</h5>
                  <span className="text-danger">{pdfFooterLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`logofooter-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && footerPdf ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.pdf_footer_logo_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.pdf_footer_logo_url}
                              alt="Company Footer Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to Pdf Footer logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.pdf_footer_logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeletePdfFooterLogo(c.id)}
                          >
                            Delete PDF Footer Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`logofooter-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handlefooterlogoFileUpload(e, c.id)
                        } /* Updated function name */
                      />
                    </div>
                  </div>
                </Card>
              </Col>
            ))
          ) : (
            <Col>
              <p className="text-center mt-5">
                No Company Information Added Yet
              </p>
            </Col>
          )}
        </Row>
      )}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {!selectedCompanyId ? "Add" : "Edit"} Company
        </ModalHeader>
        <ModalBody>
          <form onSubmit={formik.handleSubmit}>
            {(
              Object.keys(formik.initialValues) as Array<
                keyof typeof formik.values
              >
            ).map((key) => (
              <FormGroup key={key}>
                <Label for={key}>
                  {key === "title"
                    ? "Company Title"
                    : key === "company_name"
                      ? "Display Name"
                      : key
                          .replace("_", " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                  {validationSchema.describe().fields[
                    key
                    //@ts-ignore
                  ]?.tests.some((test: any) => test.name === "required") && (
                    <span className="text-danger">*</span>
                  )}
                  {key === "company_name" && (
                    <>
                      <i
                        id="displayNameTooltip"
                        className="ri-information-fill p-2 ms-1 text-secondary"
                        style={{ cursor: "pointer" }}
                      ></i>
                      <UncontrolledTooltip
                        placement="top"
                        target="displayNameTooltip"
                        innerClassName="custom-tooltip"
                      >
                        Display Name is used to show in the dropdown for all
                        Permits and Reports.
                      </UncontrolledTooltip>
                    </>
                  )}
                  {key === "company_code" && (
                    <>
                      <i
                        id="companyCodeTooltip"
                        className="ri-information-fill p-2 ms-1 text-secondary"
                        style={{ cursor: "pointer" }}
                      ></i>
                      <UncontrolledTooltip
                        placement="top"
                        target="companyCodeTooltip"
                        innerClassName="custom-tooltip"
                      >
                        Company Code is used in Permits and Reports to generate
                        the number sequence. <br /> <strong>Note:</strong> Any
                        changes may affect the Permits and Reports number
                        sequence.
                      </UncontrolledTooltip>
                    </>
                  )}
                </Label>
                <Input
                  id={key}
                  name={key}
                  value={formik.values[key]}
                  onChange={formik.handleChange}
                />
                {formik.errors[key] && formik.touched[key] && (
                  <div className="text-danger">{formik.errors[key]}</div>
                )}
              </FormGroup>
            ))}
            <ModalFooter>
              <Button
                color="primary"
                type="submit"
                disabled={postCompanyLoading || companyLoading}
              >
                Save
              </Button>
              <Button
                color="secondary"
                onClick={toggleModal}
                disabled={postCompanyLoading || companyLoading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default PrimaryCompanyTab;
